import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useMutationRenameVersion } from 'api/mutations/renameVersion.mutation';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';
import classes from 'components/widgets/RenderLibraryTable/components/RenameVersionModal/RenameVersionModal.module.scss';

interface IRenameVersionModal {
    id: number;
    versionName: string;
    showRenameVersionModal: boolean;
    setShowRenameVersionModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RenameVersionModal = ({
    id,
    versionName,
    showRenameVersionModal,
    setShowRenameVersionModal,
}: IRenameVersionModal) => {
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const {
        handleSubmit,
        setValue,
        control,
        watch,
        formState: { errors },
    } = useForm({
        values: {
            versionName: versionName,
        },
    });

    const { mutate: mutateRenameVersion, isLoading } = useMutationRenameVersion();

    const closeModal = () => {
        setShowRenameVersionModal(false);
        setValue('versionName', versionName);
    };

    const handleSave = (data) => {
        mutateRenameVersion(
            {
                metaDataid: id,
                versionName: data.versionName,
            },
            {
                onSuccess: () => {
                    handleSnackBar('Version Renamed', 'success');
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                    return queryClient.invalidateQueries('getProjectVersions');
                },
            },
        );
    };

    const isSaveDisabled = versionName === watch('versionName');

    return (
        <Modal
            open={showRenameVersionModal}
            onClose={closeModal}
            title={'Rename version'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        isLoading={isLoading}
                        disabled={isSaveDisabled}
                        onClick={handleSubmit(handleSave)}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.renameVersionModalContainer}>
                <form onSubmit={handleSubmit(handleSave)}>
                    <div className={classes.inputTitle}>Name *</div>
                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    placeholder={'Version name...'}
                                    maxLength={100}
                                    onClear={() => {
                                        setValue('versionName', '');
                                    }}
                                />
                            )}
                            name={'versionName'}
                            control={control}
                            rules={{
                                required: 'Version Name is Required',
                                minLength: {
                                    value: 3,
                                    message: 'Version Name is too short',
                                },
                                pattern: {
                                    value: /^(?!\s+$).*/,
                                    message: 'Name cannot be empty',
                                },
                            }}
                        />
                    </div>
                    {errors.versionName && <CustomizedFormErrorMessage text={errors.versionName.message} />}
                </form>
            </div>
        </Modal>
    );
};
